import { Box, styled, Typography } from "@mui/material";

import {
  formatClassPeriod,
  formatClassSchedule,
} from "@shared/lib/utils/date/formatSchedule";
import { useCustomTranslation } from "@/utils/useCustomTranslation";

interface SchedulePeriodDisplayProps {
  cycleStartsAt: Date | string;
  cycleEndsAt: Date | string;
  totalSessions: number;
  scheduleStartsAt: Date | string;
  scheduleEndsAt: Date | string;
  durationPerSession: number;
  sessionNumber: number;
  size?: "small" | "medium";
}

export const SchedulePeriodDisplay = ({
  cycleStartsAt,
  cycleEndsAt,
  totalSessions,
  scheduleStartsAt,
  scheduleEndsAt,
  durationPerSession,
  size = "medium",
  sessionNumber,
}: SchedulePeriodDisplayProps) => {
  const { t } = useCustomTranslation();

  const scheduleTime = formatClassSchedule(
    new Date(scheduleStartsAt),
    new Date(scheduleEndsAt),
    durationPerSession,
  );

  // 날짜와 시간을 분리 (요일 정보를 기준으로)
  const splitScheduleTime = (scheduleTime: string): [string, string] => {
    const dateEndIndex = scheduleTime.indexOf(")") + 1;
    const date = scheduleTime.slice(0, dateEndIndex);
    const time = scheduleTime.slice(dateEndIndex).trim();
    return [date, time];
  };

  const [date, time] = splitScheduleTime(scheduleTime);

  return (
    <Container>
      <PeriodText size={size}>
        {formatClassPeriod(
          new Date(cycleStartsAt),
          new Date(cycleEndsAt),
          totalSessions,
        )}
      </PeriodText>
      <ScheduleText size={size}>
        <DateSpan size={size}>{date}</DateSpan>
        <TimeContainer>
          <TimeSpan>{time}</TimeSpan>
          <SessionSpan>
            / {sessionNumber}
            {t("session")}
          </SessionSpan>
        </TimeContainer>
      </ScheduleText>
    </Container>
  );
};

const Container = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "4px",
});

const PeriodText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "size",
})<{ size?: "small" | "medium" }>(({ theme, size = "medium" }) => ({
  fontSize: "12px",
  fontWeight: 500,
  lineHeight: "16px",
  letterSpacing: "-0.4px",
  color: theme.palette?.text?.secondary,
}));

const ScheduleText = styled(Box, {
  shouldForwardProp: (prop) => prop !== "size",
})<{ size?: "small" | "medium" }>(({ size = "medium" }) => ({
  fontSize: "14px",
  fontWeight: 400,
  lineHeight: "20px",
  letterSpacing: "-0.4px",
  display: "flex",
  flexDirection: size === "medium" ? "column" : "row",
  gap: size === "medium" ? "2px" : "4px",
}));

const DateSpan = styled("span", {
  shouldForwardProp: (prop) => prop !== "size",
})<{ size?: "small" | "medium" }>(({ size = "medium" }) => ({
  display: "block",
}));

const TimeContainer = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: "4px",
});

const TimeSpan = styled("span")({
  display: "inline-block",
});

const SessionSpan = styled("span")({
  display: "inline-block",
});
